import { shapes } from '@joint/core';
import { getIconPath } from '../shared/helpers.js';
import { formatDisplayName } from '../shared/displayName.js';
import { BREAK_ACCENT_COLOR } from './statusTooltip.js';

/** Port magnet size + gap used for dynamic icon height. */
const PORT_STEP = 24;
const PORT_PAD = 12;
const ICON_MIN_HEIGHT = 62;
/** Collapsed icon width (popup overlay is independent and wider). */
const ICON_WIDTH = 146; // 122 * 1.2
/** Rounded well behind the MDI glyph (palette-tile style). */
const ICON_WELL_SIZE = 32;
const ICON_WELL_RADIUS = 8;
const ICON_GLYPH_SIZE = 20;
/** Left-header layout: mdi well, then title + caption stacked beside it. */
const HEADER_ICON_SIZE = ICON_WELL_SIZE;
const HEADER_PAD = 8;
const HEADER_TEXT_X = HEADER_PAD + HEADER_ICON_SIZE + 6;
/** Right inset for title/caption — badges live on the well, not the title row. */
const HEADER_TEXT_WIDTH = ICON_WIDTH - HEADER_TEXT_X - 8;
/** Well bottom-right, inset so a two-line caption does not collide. */
const STATUS_BADGE_X = HEADER_PAD + ICON_WELL_SIZE - 8;
const STATUS_BADGE_Y = HEADER_PAD + ICON_WELL_SIZE;
const BREAK_ACCENT_WIDTH = 4;
/** Y of the faded header separator (room for two-line caption under title). */
const HEADER_SEP_Y = HEADER_PAD + HEADER_ICON_SIZE + 14;
/** Ports must sit entirely below this Y so they never collide with the header. */
const HEADER_CLEARANCE = HEADER_SEP_Y + 10;
/**
 * Label sits `end`-anchored 12px left of the right-edge port; keep 8px off the body left.
 * Do not use JointJS textWrap here — port label bbox is ~0 and wrap wipes the text.
 */
const PORT_LABEL_MAX_WIDTH = ICON_WIDTH - HEADER_PAD - 12;
const PORT_LABEL_FONT = '9px Roboto, Arial, sans-serif';
/** Fallback if canvas measureText is unavailable. */
const PORT_LABEL_MAX_CHARS = Math.max(16, Math.floor(PORT_LABEL_MAX_WIDTH / 4.2));

/**
 * Icon height so the header band stays clear and all right-edge ports fit.
 * @param {number} portCount
 * @returns {number}
 */
function computeIconHeight(portCount) {
    if (!portCount || portCount < 1)
        return Math.max(ICON_MIN_HEIGHT, HEADER_CLEARANCE + PORT_PAD);
    // Bottom-packed stack must clear HEADER_CLEARANCE at the topmost port
    const stackH = PORT_PAD + portCount * PORT_STEP;
    return Math.max(ICON_MIN_HEIGHT, HEADER_CLEARANCE + stackH);
}

/**
 * Y position for a port stacked on the right edge, packed toward the bottom.
 * Index 0 is highest in the stack; last index sits nearest the bottom corner.
 * @param {number} index
 * @param {number} portCount
 * @param {number} iconHeight
 * @returns {number}
 */
function computePortYFromBottom(index, portCount, iconHeight) {
    const n = Math.max(1, portCount || 1);
    const i = Math.max(0, Math.min(index, n - 1));
    // Center of port magnet: bottom pad + (ports below this one + half) * step
    return iconHeight - PORT_PAD - (n - i - 0.5) * PORT_STEP;
}

/**
 * Reposition all ports on an element to the right edge, bottom-aligned for current size.
 * Prefers Model.Links order so stacking matches addFlowItem.
 * @param {object} element JointJS element
 */
function layoutIconPorts(element) {
    if (!element?.getPorts || !element.portProp)
        return;
    const size = element.size();
    const ports = element.getPorts();
    if (!ports.length)
        return;

    let ordered = ports;
    const linkDefs = element.prop?.('prop/Model/Links') ?? element.get?.('prop')?.Model?.Links;
    if (Array.isArray(linkDefs) && linkDefs.length) {
        const byId = new Map(ports.map(p => [p.id, p]));
        ordered = [];
        for (const link of linkDefs) {
            const p = byId.get(link.Name);
            if (p) {
                ordered.push(p);
                byId.delete(link.Name);
            }
        }
        for (const p of byId.values())
            ordered.push(p);
    }

    const n = ordered.length;
    for (let i = 0; i < n; i++) {
        element.portProp(ordered[i].id, 'args', {
            x: size.width,
            y: computePortYFromBottom(i, n, size.height),
        });
    }
}

let portLabelMeasureCtx = null;
function getPortLabelMeasureCtx() {
    if (portLabelMeasureCtx)
        return portLabelMeasureCtx;
    if (typeof document === 'undefined')
        return null;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return null;
    ctx.font = PORT_LABEL_FONT;
    portLabelMeasureCtx = ctx;
    return ctx;
}

/**
 * Port label shown left of the magnet (right-justified). Truncates with an ellipsis
 * to fit PORT_LABEL_MAX_WIDTH (canvas measureText; char cap is fallback only).
 * @param {string} text
 * @returns {string}
 */
function formatPortLabel(text) {
    const t = formatDisplayName(text);
    if (!t)
        return '';
    const ell = '\u2026';
    const ctx = getPortLabelMeasureCtx();
    if (!ctx) {
        if (t.length <= PORT_LABEL_MAX_CHARS)
            return t;
        return `${t.slice(0, Math.max(1, PORT_LABEL_MAX_CHARS - 1))}${ell}`;
    }
    if (ctx.measureText(t).width <= PORT_LABEL_MAX_WIDTH)
        return t;
    let lo = 1;
    let hi = t.length;
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (ctx.measureText(t.slice(0, mid) + ell).width <= PORT_LABEL_MAX_WIDTH)
            lo = mid;
        else
            hi = mid - 1;
    }
    return `${t.slice(0, Math.max(1, lo))}${ell}`;
}

/** Absolute right-edge ports with tooltip text as a left / right-justified label. */
const rightEdge_port = {
    position: { name: 'absolute' },
    // Keep the label outside the port-body markup so link magnets use the icon bbox only
    // (inlined <text> inflates the magnet and routes through the label).
    label: {
        position: {
            name: 'left',
            args: { x: -12 },
        },
        markup: [{
            tagName: 'text',
            selector: 'labelText',
        }],
    },
    attrs: {
        portBody: {
            magnet: true,
            width: 18,
            height: 18,
            x: -8,
            y: -9,
            fill: '#FFFFFF',
            stroke: '#4285F4',
            strokeWidth: 0.2,
            cursor: 'crosshair'
        },
        portIcon: {
            d: getIconPath('mdi-arrow-right-bold'),
            transform: 'scale(0.75) translate(-10, -12)',
            stroke: '#4285F4',
            pointerEvents: 'none',
        },
        portTooltip: {
            text: 'Default Tooltip'
        },
        // No textWrap — wrap against a zero port/label bbox wipes the text.
        labelText: {
            text: '',
            fontSize: 9,
            fill: '#5F6368',
            fontFamily: 'Roboto, Arial, sans-serif',
            pointerEvents: 'none',
            textVerticalAnchor: 'middle',
            textAnchor: 'end',
        },
    },
    markup: [
        {
            tagName: 'g', className: 'port', children: [
                { tagName: 'rect', selector: 'portBody' },
                { tagName: 'path', selector: 'portIcon' },
                { tagName: 'title', selector: 'portTooltip' }
            ]
        }
    ]
};

const IVRElement = shapes.standard.Rectangle.define('standard.MethodElement', {
    markup: [{
        tagName: 'rect',
        selector: 'body',
    }, {
        tagName: 'rect',
        selector: 'breakAccent',
    }, {
        tagName: 'rect',
        selector: 'iconWell',
    }, {
        tagName: 'path',
        selector: 'icon',
    }, {
        tagName: 'g',
        selector: 'statusBadge',
        children: [
            { tagName: 'circle', selector: 'statusHit' },
            { tagName: 'path', selector: 'statusIcon' },
            { tagName: 'title', selector: 'statusTooltip' },
        ],
    }, {
        tagName: 'rect',
        selector: 'headerSeparator',
    }, {
        tagName: 'text',
        selector: 'label',
    }, {
        tagName: 'text',
        selector: 'title',
    }],
    attrs: {
        root: {
            magnetSelector: 'body',
        },
        // Palette-style rounded well behind the MDI glyph
        iconWell: {
            x: HEADER_PAD,
            y: HEADER_PAD,
            width: ICON_WELL_SIZE,
            height: ICON_WELL_SIZE,
            rx: ICON_WELL_RADIUS,
            ry: ICON_WELL_RADIUS,
            fill: '#e8eef4',
            stroke: 'none',
        },
        // MDI glyph: centered in the well (MDI paths are 24×24)
        icon: {
            d: '',
            transform: `scale(${ICON_GLYPH_SIZE / 24}) translate(-12, -12)`,
            fill: '#333333',
            refX: HEADER_PAD + ICON_WELL_SIZE / 2,
            refY: HEADER_PAD + ICON_WELL_SIZE / 2,
        },
        // Breakpoint: 4px left rail (follows body corner radius)
        breakAccent: {
            x: 0,
            y: 0,
            width: BREAK_ACCENT_WIDTH,
            refHeight: '100%',
            rx: 5,
            ry: 5,
            fill: BREAK_ACCENT_COLOR,
            visibility: 'hidden',
            pointerEvents: 'none',
        },
        statusBadge: {
            visibility: 'hidden',
        },
        statusHit: {
            r: 14,
            fill: 'transparent',
            refX: STATUS_BADGE_X,
            refY: STATUS_BADGE_Y,
            visibility: 'hidden',
        },
        // Compile-error chip on the well bottom-right (~22px; 24×24 MDI at 0.9)
        statusIcon: {
            d: getIconPath('mdi-alert-circle-outline'),
            transform: 'scale(0.9) translate(-12, -12)',
            fill: '#ff3333',
            refX: STATUS_BADGE_X,
            refY: STATUS_BADGE_Y,
            visibility: 'hidden',
        },
        statusTooltip: {
            text: '',
        },
        // Soft horizontal rule under the header — inset, fades toward both edges
        headerSeparator: {
            x: 14,
            y: HEADER_SEP_Y,
            width: 'calc(w - 28)',
            height: 1,
            stroke: 'none',
            pointerEvents: 'none',
            fill: {
                type: 'linearGradient',
                stops: [
                    { offset: '0%', color: 'rgba(66,133,244,0)' },
                    { offset: '18%', color: 'rgba(66,133,244,0.38)' },
                    { offset: '50%', color: 'rgba(66,133,244,0.5)' },
                    { offset: '82%', color: 'rgba(66,133,244,0.38)' },
                    { offset: '100%', color: 'rgba(66,133,244,0)' },
                ],
            },
        },
        body: {
            refWidth: '100%',
            refHeight: '100%',
            rx: 5,
            fill: {
                type: 'linearGradient',
                stops: [
                    { offset: '0%', color: '#F5F5F5' },
                    { offset: '100%', color: '#E8F0FE' }
                ]
            },
            stroke: '#4285F4',
            strokeWidth: 1,
            filter: {
                name: 'dropShadow',
                args: { dx: 0, dy: 2, blur: 3, opacity: 0.2 }
            }
        },
        // Title beside the mdi icon (top of the text stack).
        // Use x/y — must override standard.Rectangle label defaults (calc(w/2), calc(h/2)).
        title: {
            text: 'TYPE',
            fontSize: 11,
            fontWeight: 'bold',
            fill: '#1967D2',
            x: HEADER_TEXT_X,
            y: HEADER_PAD + 2,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            textTransform: 'uppercase',
            textWrap: {
                width: HEADER_TEXT_WIDTH,
                maxLineCount: 1,
                ellipsis: true,
            },
        },
        // Caption as subtitle under the title; wraps to two lines then ellipsis.
        // Override inherited Rectangle label x/y: calc(w/2), calc(h/2).
        label: {
            text: 'Custom Caption',
            fontSize: 9,
            fill: '#666666',
            x: HEADER_TEXT_X,
            y: HEADER_PAD + 15,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            textWrap: {
                width: HEADER_TEXT_WIDTH,
                maxLineCount: 2,
                ellipsis: true,
            },
        },
        pauseHighlight: {
            name: 'stroke',
            args: {
                stroke: '#FFFF00',
                strokeWidth: 4,
                padding: 4
            }
        },
    },
    ports: {
        groups: {
            // One right-edge group only — former "results" ports use this group too
            default: rightEdge_port,
            results: rightEdge_port,
        },
    }
});

/** Always the right-edge group (Category remains on customData.Link). */
function getPortGroup(_category) {
    return rightEdge_port;
}

export {
    IVRElement,
    getPortGroup,
    computeIconHeight,
    computePortYFromBottom,
    layoutIconPorts,
    formatPortLabel,
    PORT_STEP,
    PORT_PAD,
    ICON_MIN_HEIGHT,
    ICON_WIDTH,
    HEADER_CLEARANCE,
    BREAK_ACCENT_WIDTH,
};
